import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import "./AccountingPage.css";
import SisLifecyclePanel from "./SisLifecyclePanel";
const input =
  "mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white px-3";
const button =
  "rounded-xl bg-primary-700 px-5 py-3 text-white disabled:opacity-50";
export default function SisLiabilities() {
  const { accessToken, user } = useAuth(),
    canPost = hasPermission(user, "accounting.vouchers_post");
  const [companies, setCompanies] = useState([]),
    [company, setCompany] = useState(""),
    [q, setQ] = useState(""),
    [candidates, setCandidates] = useState([]),
    [id, setId] = useState(""),
    [data, setData] = useState(null),
    [cost, setCost] = useState(""),
    [note, setNote] = useState(""),
    [preview, setPreview] = useState(null),
    [checks, setChecks] = useState({}),
    [record, setRecord] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const epoch = useRef(0),
    busyRef = useRef(false),
    retry = useRef(null);
  function invalidate() {
    epoch.current++;
    busyRef.current = false;
    setBusy(false);
    setPreview(null);
    setChecks({});
    retry.current = null;
    setError("");
    setNotice("");
  }
  useEffect(() => {
    let active = true;
    invalidate();
    setCompanies([]);
    setCompany("");
    setCandidates([]);
    setId("");
    setData(null);
    setRecord(null);
    setCost("");
    setNote("");
    setQ("");
    apiRequest("/accounting/companies", { accessToken })
      .then((rows) => {
        if (active) {
          setCompanies(rows);
          setCompany(rows[0]?.id || "");
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
      epoch.current++;
    };
  }, [accessToken]);
  async function action(job, apply) {
    if (busyRef.current) return;
    const ticket = epoch.current;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await job();
      if (ticket === epoch.current) apply(result);
    } catch (e) {
      if (ticket === epoch.current)
        setError(e.message || "Unable to complete this request");
    } finally {
      if (ticket === epoch.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }
  const base = `/accounting/${company}/supplier-liabilities/sis`;
  function changeCompany(value) {
    invalidate();
    setCompany(value);
    setCandidates([]);
    setId("");
    setData(null);
    setRecord(null);
    setCost("");
    setNote("");
  }
  function choose(value) {
    invalidate();
    setId(value);
    setData(null);
    setRecord(null);
    setCost("");
    setNote("");
  }
  function search(e) {
    e.preventDefault();
    invalidate();
    setCandidates([]);
    setId("");
    setData(null);
    setRecord(null);
    action(
      () =>
        apiRequest(`${base}/candidates?q=${encodeURIComponent(q)}`, {
          accessToken,
        }),
      setCandidates,
    );
  }
  function load() {
    invalidate();
    setData(null);
    setRecord(null);
    action(
      () => apiRequest(`${base}/pos/${id}`, { accessToken }),
      (result) => {
        setData(result);
        setRecord(result.recognition);
      },
    );
  }
  function propose(e) {
    e.preventDefault();
    setPreview(null);
    setChecks({});
    retry.current = null;
    action(
      () =>
        apiRequest(`${base}/pos/${id}/preview`, {
          method: "POST",
          accessToken,
          body: { costLedgerId: cost, reviewNote: note },
        }),
      setPreview,
    );
  }
  function post() {
    action(
      async () => {
        if (!retry.current) {
          if (!window.crypto?.randomUUID)
            throw Error(
              "Secure UUID support is required for safe financial retries",
            );
          retry.current = window.crypto.randomUUID();
        }
        return apiRequest(`${base}/pos/${id}`, {
          method: "POST",
          accessToken,
          body: {
            costLedgerId: cost,
            reviewNote: note,
            previewHash: preview.previewHash,
            requestKey: retry.current,
            priorEntriesReviewed: checks.prior === true,
            grossCostNoItcConfirmed: checks.tax === true,
            correctionRestrictionAccepted: checks.correction === true,
          },
        });
      },
      (result) => {
        setRecord(result);
        setPreview(null);
        setNotice(
          "SIS cost and supplier bills posted. No payment was sent. Use Party Accounts to allocate a posted payment.",
        );
      },
    );
  }
  const ledgers = data?.workspace?.ledgers || [],
    bills = record?.snapshot?.bills || [];
  return (
    <main className="accounting-master space-y-6">
      <header>
        <p className="text-sm text-gray-500">
          Accounting / Supplier liabilities
        </p>
        <h1 className="text-3xl font-bold">SIS supplier liabilities</h1>
        <p className="mt-2 text-gray-600">
          Recognise reviewed sales-based commercial dues using retained
          agreements. Settle generated bills separately.
        </p>
      </header>
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Supported: inclusive margin, brand product discounts, CHARMS monetary
        loyalty discount, monthly next-month-day dues and an explicitly reviewed
        gross-cost/no-input-credit policy. Post CHARMS sales/GST through Sales
        Reviews, then use the dedicated invoice-match and customer-credit
        controls below. Ordinary source edits and generic reversals stay locked.
        Points, coupons, commissions, rounding and unsupported stock valuations
        need separate coordinated policies.
      </div>
      {error && (
        <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-800">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-xl bg-green-50 p-4 text-green-800">
          {notice}
        </p>
      )}
      <form
        onSubmit={search}
        className="grid gap-4 rounded-2xl bg-white p-6 md:grid-cols-2"
      >
        <label>
          Accounting company
          <select
            className={input}
            value={company}
            onChange={(e) => changeCompany(e.target.value)}
            disabled={busy}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Original POS receipt
          <input
            className={input}
            value={q}
            minLength={2}
            required
            onChange={(e) => setQ(e.target.value)}
            disabled={busy}
          />
        </label>
        <button className={button} disabled={busy || !company}>
          Find receipts
        </button>
        <p className="self-center text-xs text-gray-500">
          Latest 50 matching original sales in mapped stores. Eligibility is
          checked when loaded.
        </p>
      </form>
      {!!candidates.length && (
        <section className="flex gap-4 rounded-2xl bg-white p-6">
          <label className="flex-1">
            Select receipt
            <select
              className={input}
              value={id}
              onChange={(e) => choose(e.target.value)}
              disabled={busy}
            >
              <option value="">Choose receipt</option>
              {candidates.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.receiptNumber}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={button}
            disabled={busy || !id}
            onClick={load}
          >
            Load original source
          </button>
        </section>
      )}
      {data && (
        <section className="space-y-4 rounded-2xl bg-white p-6">
          <h2 className="text-xl font-semibold">
            {data.source.source.receiptNumber} · {data.source.source.sourceDate}
          </h2>
          {data.source.blockers.map((b, i) => (
            <p key={i} className="text-sm text-red-700">
              {b}
            </p>
          ))}
          {!record && !data.source.blockers.length && (
            <form onSubmit={propose} className="space-y-4">
              <label className="block">
                Reviewed gross SIS cost expense ledger
                <select
                  required
                  className={input}
                  value={cost}
                  disabled={busy}
                  onChange={(e) => {
                    invalidate();
                    setCost(e.target.value);
                  }}
                >
                  <option value="">
                    Choose an accountant-reviewed expense ledger
                  </option>
                  {ledgers
                    .filter(
                      (l) =>
                        l.kind === "GENERAL" && l.group?.nature === "EXPENSE",
                    )
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.code} · {l.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="block">
                Invoice timing and prior-entry review
                <textarea
                  required
                  minLength={20}
                  maxLength={1000}
                  className="mt-2 w-full rounded-xl border p-3"
                  value={note}
                  disabled={busy}
                  onChange={(e) => {
                    invalidate();
                    setNote(e.target.value);
                  }}
                />
              </label>
              <button className={button} disabled={busy || !cost}>
                Preview liability posting
              </button>
            </form>
          )}
        </section>
      )}
      {preview && !record && (
        <section className="space-y-4 rounded-2xl bg-white p-6">
          <h2 className="text-xl font-semibold">
            Actual posting proposal · ₹{preview.snapshot.total}
          </h2>
          <p className="text-sm text-amber-800">{preview.notice}</p>
          <table>
            <thead>
              <tr>
                <th>Ledger</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {preview.snapshot.lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    {ledgers.find((x) => x.id === l.ledgerId)?.name ||
                      l.ledgerId}
                  </td>
                  <td>{l.debit}</td>
                  <td>{l.credit}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm">
            Due dates:{" "}
            {preview.snapshot.bills
              .map((b) => `${b.number}: ${b.dueDate}`)
              .join(" · ")}
          </p>
          {canPost && (
            <>
              <label className="flex gap-3 text-sm">
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={!!checks.prior}
                  onChange={(e) =>
                    setChecks({ ...checks, prior: e.target.checked })
                  }
                />
                I reviewed the original source, supplier invoice timing and
                prior/manual entries; this cost/payable is not already recorded.
              </label>
              <label className="flex gap-3 text-sm">
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={!!checks.tax}
                  onChange={(e) =>
                    setChecks({ ...checks, tax: e.target.checked })
                  }
                />
                I explicitly approve gross commercial cost accrual without input
                GST credit. Actual supplier invoice/tax adjustments require
                separate review and must not duplicate this cost.
              </label>
              <label className="flex gap-3 text-sm">
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={!!checks.correction}
                  onChange={(e) =>
                    setChecks({ ...checks, correction: e.target.checked })
                  }
                />
                I accept that ordinary edits and generic reversal stay locked;
                supported returns and credit-and-reissue use the dedicated SIS
                workflow.
              </label>
              <button
                type="button"
                className={button}
                disabled={
                  busy || !checks.prior || !checks.tax || !checks.correction
                }
                onClick={post}
              >
                Post SIS liability
              </button>
            </>
          )}
        </section>
      )}
      {record && (
        <section className="space-y-4 rounded-2xl border border-green-200 bg-white p-6">
          <h2 className="text-xl font-semibold">
            {record.cancellation
              ? "Cancelled supplier liability original history"
              : "Recognised supplier liability"}
          </h2>
          <p>Voucher ID: {record.voucherId}</p>
          <table>
            <thead>
              <tr>
                <th>Bill reference</th>
                <th>Due date</th>
                <th>Original amount</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.number}>
                  <td>{b.number}</td>
                  <td>{b.dueDate}</td>
                  <td>₹{b.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {record.cancellation ? (
            <p className="text-sm text-amber-900">
              Cancelled on {String(record.cancellation.date).slice(0, 10)} by
              voucher{" "}
              {record.cancellation.snapshot?.reversalNumber ||
                record.cancellation.voucherId}
              . These original bill amounts are historical and must not be paid
              or allocated as active dues. No replacement accrual has been
              posted.
            </p>
          ) : (
            <p className="text-sm">
              These are original bill amounts, not current outstanding balances.
              Record a real payment using Payment Vouchers, then allocate its
              party reference to these bills in{" "}
              <a
                className="text-primary-800 underline"
                href="/admindashboard/accounting/party-accounts"
              >
                Party Accounts
              </a>
              . Partial/multi-bill allocations and dated releases use the
              existing controls. This screen does not send money.
            </p>
          )}
        </section>
      )}
      {record && data && (
        <SisLifecyclePanel
          key={company + id}
          company={company}
          saleId={id}
          record={record}
          workspace={data.workspace}
          onCancelled={(row) =>
            setRecord((current) =>
              current ? { ...current, cancellation: row } : current,
            )
          }
        />
      )}
    </main>
  );
}
